// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import _ from 'lodash';

// Plugins
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  isEquipmentRunning,
  lubricationFlowStatusToTagType,
  lubricationFlowStatusToText,
  overallStatusToTagType,
  overallStatusToText,
  permissiveStatusToIndicatorStatus,
  runningEnabledStatusToIndicatorStatus,
  runningStatusToIndicatorStatus,
  weatherModeStatusToTagType,
  weatherModeStatusToText,
} from '../../../../../../plugins/crusher';
import { convertTemperature } from '../../../../../../plugins/general';
import { TEMPERATURE_RANGE } from '../../../../../../plugins/thresholds';

// Types
import { CrusherRunningEStatus, CrusherRunningSStatus } from '../../../../../../types/crusher';
import { Theme } from '../../../../../../types/theme';
import {
  MeasurementType,
  TemperatureType,
} from '../../../../../../types/global';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../store/measurement.store';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Components
import { Button, ButtonSize, ButtonType } from '../../../../../../layout/elements/Button';
import { Col } from '../../../../../../layout/elements/Col';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import {
  StatusIndicator,
  StatusIndicatorSize,
} from '../../../../../../layout/elements/StatusIndicator';
import { Tag } from '../../../../../../layout/elements/Tag';
import { Thermometer } from '../../../../../../layout/elements/Thermometer';

// View
import { LubricationUnitProps } from './LubricationUnit.types';
import {
  BorderBottomContainer,
  LubePermissivesContainer,
  LubeTankLevelContainer,
  LubricationContainer,
  LubricationSupplyFlowContainer,
  OverallStatusContainer,
  PermissiveItem,
  PermissiveListContainer,
  PermissiveTitleContainer,
  RunningStatusContainer,
  RunningStatusList,
  RunningStatusPermissive,
  ThermometerContainer,
  WinterModeContainer,
  WinterModeStatus,
} from './LubricationUnit.style';

function LubricationUnit({
  className,
  edgeStatus,
  testId,
  unitData,
}: LubricationUnitProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { lubricationUnitDrawer } = theme.color.crusherDashboard;
  const {
    isXs,
    isXl,
    isXxl,
  } = useBreakpoint();

  const userTemperatureUnit: TemperatureType = getMeasurementUnit(
    MeasurementType.temperature,
  );

  /**
   * Render a permissive item with the given name and value.
   * @param permissiveName - The name of the permissive to render.
   * @param permissive - The permissive to render.
   * @returns A JSX element.
   */
  const renderPermissive = (
    permissiveName: string,
    permissive: boolean | null | undefined,
  ): JSX.Element => (
    <PermissiveItem
      status={permissiveStatusToIndicatorStatus(
        permissive,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      )}
      data-testid={`${testId}-permissive-item`}
    >
      <StatusIndicator
        text={t(`view.crusher.vsi.drawers.lubrication.${permissiveName}`)}
        status={permissiveStatusToIndicatorStatus(
          permissive,
          edgeStatus ? edgeStatus.edgeStatus : null,
          unitData?.timestamp,
        )}
      />
    </PermissiveItem>
  );

  /**
   * Render a running status permissive item with the given name and value.
   * @param runningStatusName - The name of the running status to render.
   * @param runningStatus - The running status to render (CrusherRunningSStatus).
   * @returns A JSX element.
   */
  const renderRunningStatusPermissive = (
    runningStatusName: string,
    runningStatus: CrusherRunningSStatus | null | undefined,
  ): JSX.Element => (
    <RunningStatusPermissive
      status={runningStatusToIndicatorStatus(
        runningStatus,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      )}
    >
      <StatusIndicator
        text={t(`view.crusher.vsi.drawers.lubrication.${runningStatusName}`)}
        status={runningStatusToIndicatorStatus(
          runningStatus,
          edgeStatus ? edgeStatus.edgeStatus : null,
          unitData?.timestamp,
        )}
      />
    </RunningStatusPermissive>
  );

  /**
   * Render a enabled status permissive item with the given name and value.
   * @param enabledStatusName - The name of the enabled status to render.
   * @param enabledStatus - The enabled status to render (CrusherRunningEStatus).
   * @returns A JSX element.
   */
  const enabledStatusPermissive = (
    enabledStatusName: string,
    enabledStatus: CrusherRunningEStatus | null | undefined,
  ): JSX.Element => (
    <RunningStatusPermissive
      status={runningEnabledStatusToIndicatorStatus(
        enabledStatus,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      )}
    >
      <StatusIndicator
        text={t(`view.crusher.vsi.drawers.lubrication.${enabledStatusName}`)}
        status={runningEnabledStatusToIndicatorStatus(
          enabledStatus,
          edgeStatus ? edgeStatus.edgeStatus : null,
          unitData?.timestamp,
        )}
      />
    </RunningStatusPermissive>
  );

  return (
    <LubricationContainer data-testid={testId} className={className}>
      <BorderBottomContainer>
        <Row>
          <Col>
            <OverallStatusContainer data-testid={`${testId}-overall-status-container`}>
              <Paragraph
                color={lubricationUnitDrawer.overallStatusDescriptionTextColor}
                weight={ParagraphWeight.bold}
                size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                testId={`${testId}-overall-status-description`}
              >
                {t('view.crusher.overallStatus')}
              </Paragraph>
              <Tag
                text={overallStatusToText(unitData?.lubricationOverallStatus)}
                type={
                  overallStatusToTagType(
                    unitData?.lubricationOverallStatus,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
              />
            </OverallStatusContainer>
          </Col>

          <Col xs={24} xl={12}>
            <LubeTankLevelContainer data-testid={`${testId}-lube-tank-level-container`}>
              <Paragraph
                color={lubricationUnitDrawer.overallStatusDescriptionTextColor}
                weight={ParagraphWeight.bold}
                size={isXs ? ParagraphSize.sm : ParagraphSize.md}
              >
                {t('view.crusher.vsi.drawers.lubrication.lubeTankLevel')}
              </Paragraph>
              <Tag
                text={overallStatusToText(unitData?.lubricationTankLevelStatus)}
                type={
                  overallStatusToTagType(
                    unitData?.lubricationTankLevelStatus,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
              />
            </LubeTankLevelContainer>
          </Col>

          <Col xs={24} xl={12}>
            <LubricationSupplyFlowContainer data-testid={`${testId}-supply-flow-container`}>
              <Paragraph
                color={lubricationUnitDrawer.overallStatusDescriptionTextColor}
                weight={ParagraphWeight.bold}
                size={isXs ? ParagraphSize.sm : ParagraphSize.md}
              >
                {t('view.crusher.vsi.drawers.lubrication.lubricationSupplyFlow')}
              </Paragraph>
              <Tag
                text={lubricationFlowStatusToText(unitData?.lubricationSupplyFlowStatus)}
                type={
                  lubricationFlowStatusToTagType(
                    unitData?.lubricationSupplyFlowStatus,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
              />
            </LubricationSupplyFlowContainer>
          </Col>
        </Row>
      </BorderBottomContainer>

      <Row>
        <Col xs={24} xl={12}>
          <RunningStatusContainer data-testid={`${testId}-running-status-container`}>
            <Paragraph
              weight={ParagraphWeight.bold}
              size={isXs ? ParagraphSize.sm : ParagraphSize.md}
            >
              {t('view.crusher.vsi.drawers.lubrication.runningStatus')}
            </Paragraph>
            <RunningStatusList>
              {enabledStatusPermissive('tankHeater', unitData?.tankLubeHeaterStatus)}
              {renderRunningStatusPermissive('lubePump', unitData?.lubePumpRunningStatus)}
              {renderRunningStatusPermissive('lubeCooler', unitData?.coolerFanStatus)}
            </RunningStatusList>
          </RunningStatusContainer>

          <PermissiveListContainer
            borderBottom={!isXl && !isXxl}
            data-testid={`${testId}-heater-permissive-container`}
          >
            <PermissiveTitleContainer>
              <StatusIndicator
                text={t('view.crusher.vsi.drawers.lubrication.lubeTankHeater')}
                size={StatusIndicatorSize.lg}
                status={
                  permissiveStatusToIndicatorStatus(
                    unitData?.lubeHeaterPermissive,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
                testId={`${testId}-heater-permissive-indicator`}
              />
            </PermissiveTitleContainer>
            {renderPermissive('lubeHeaterStarterFault', unitData?.lubeHeaterStarterFault)}
            {renderPermissive('lubeTankLevel', unitData?.lubeTankLevel)}
            {renderPermissive(
              'lubeTankTempTransducerFault',
              unitData?.lubeTankTempTransducerFault,
            )}
          </PermissiveListContainer>
        </Col>

        <Col xs={24} xl={12}>
          <LubePermissivesContainer data-testid={`${testId}-lube-permissive-container`}>
            <PermissiveTitleContainer>
              <StatusIndicator
                text={t('view.crusher.vsi.drawers.lubrication.lubePermissive')}
                size={StatusIndicatorSize.lg}
                status={
                  permissiveStatusToIndicatorStatus(
                    unitData?.lubePermissive,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
                testId={`${testId}-lube-permissive-indicator`}
              />
            </PermissiveTitleContainer>
            {renderPermissive('lubePump1StarterFault', unitData?.lubePump1StarterFault)}
            {renderPermissive('lowLubeOilGPM1', unitData?.lowLubeOilGPM1)}
            {renderPermissive('lowLubeOilGPM2', unitData?.lowLubeOilGPM2)}
            {renderPermissive('heaterEnabled', unitData?.lubeTankHeaterEnable)}
            {renderPermissive('lubeTankOilLevel', unitData?.lubeTankOilLevel)}
            {renderPermissive('tankTempTooLowToRun', unitData?.tankTempTooLowToRun)}
            {renderPermissive('tankTempTooLowToStart', unitData?.tankTempTooLowToStart)}
          </LubePermissivesContainer>
        </Col>
      </Row>

      <Row style={{ width: 'calc(100% + 1px)' }}>
        <ThermometerContainer
          xs={unitData?.lubeSupplyTempEnable ? 8 : 12}
          lg={unitData?.lubeSupplyTempEnable ? 8 : 12}
          divider
          testId={`${testId}-thermometer-return-temp-container`}
        >
          <Thermometer
            className="thermometer"
            value={
              convertTemperature(
                unitData?.lubricationReturnTemperature,
                userTemperatureUnit,
                1,
              ).value
            }
            minValue={
              _.isNil(unitData?.lubricationReturnTemperature)
                ? null
                : TEMPERATURE_RANGE(userTemperatureUnit).minValue
            }
            maxValue={
              _.isNil(unitData?.lubricationReturnTemperature)
                ? null
                : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
            }
            unit={userTemperatureUnit}
            title={`${t('view.crusher.vsi.lubrication.returnTemp')}`}
            running={
              isEquipmentRunning(
                edgeStatus,
                unitData?.timestamp,
                DEVICE_COMMUNICATION_THRESHOLD,
              )
            }
            thresholds={{
              lowAlarm: convertTemperature(
                unitData?.spLubeReturnTempLowAlarm,
                userTemperatureUnit,
              ).value,
              lowWarning: convertTemperature(
                unitData?.spLubeReturnTempLowWarning,
                userTemperatureUnit,
              ).value,
              highWarning: convertTemperature(
                unitData?.spLubeReturnTempHighWarn,
                userTemperatureUnit,
              ).value,
              highAlarm: convertTemperature(
                unitData?.spLubeReturnTempStopFeed,
                userTemperatureUnit,
              ).value,
            }}
          />
        </ThermometerContainer>

        <ThermometerContainer
          xs={unitData?.lubeSupplyTempEnable ? 8 : 12}
          lg={unitData?.lubeSupplyTempEnable ? 8 : 12}
          divider
          testId={`${testId}-thermometer-tank-temp-container`}
        >
          <Thermometer
            className="thermometer"
            value={
              convertTemperature(
                unitData?.lubricationTankTemperature,
                userTemperatureUnit,
                1,
              ).value
            }
            minValue={
              _.isNil(unitData?.lubricationTankTemperature)
                ? null
                : TEMPERATURE_RANGE(userTemperatureUnit).minValue
            }
            maxValue={
              _.isNil(unitData?.lubricationTankTemperature)
                ? null
                : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
            }
            unit={userTemperatureUnit}
            title={`${t('view.crusher.vsi.lubrication.tankTemp')}`}
            running={
              isEquipmentRunning(
                edgeStatus,
                unitData?.timestamp,
                DEVICE_COMMUNICATION_THRESHOLD,
              )
            }
            thresholds={{
              lowAlarm: convertTemperature(
                unitData?.spLubeTankTempLowAlarm,
                userTemperatureUnit,
              ).value,
              lowWarning: convertTemperature(
                unitData?.spLubeTankTempLowWarning,
                userTemperatureUnit,
              ).value,
              highWarning: convertTemperature(
                unitData?.spLubeTankTempHighWarning,
                userTemperatureUnit,
              ).value,
              highAlarm: convertTemperature(
                unitData?.spLubeTankTempHighAlarm,
                userTemperatureUnit,
              ).value,
            }}
          />
        </ThermometerContainer>

        {unitData?.lubeSupplyTempEnable && (
          <ThermometerContainer
            xs={8}
            lg={8}
            testId={`${testId}-thermometer-supply-temp-container`}
          >
            <Thermometer
              className="thermometer"
              testId="supply-temp-thermometer"
              value={
                convertTemperature(
                  unitData?.lubricationSupplyTemperature,
                  userTemperatureUnit,
                  1,
                ).value
              }
              minValue={
                _.isNil(unitData?.lubricationSupplyTemperature)
                  ? null
                  : TEMPERATURE_RANGE(userTemperatureUnit).minValue
              }
              maxValue={
                _.isNil(unitData?.lubricationSupplyTemperature)
                  ? null
                  : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
              }
              unit={userTemperatureUnit}
              title={`${t('view.crusher.vsi.lubrication.supplyTemp')}`}
              running={
                isEquipmentRunning(
                  edgeStatus,
                  unitData?.timestamp,
                  DEVICE_COMMUNICATION_THRESHOLD,
                )
              }
            />
          </ThermometerContainer>
        )}
      </Row>

      <Row>
        <Col>
          <WinterModeContainer data-testid={`${testId}-winter-mode-container`}>
            <WinterModeStatus>
              <Paragraph
                size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                weight={ParagraphWeight.bold}
                color={lubricationUnitDrawer.winterModeTextColor}
              >
                {t('view.crusher.vsi.drawers.lubrication.winterMode')}
              </Paragraph>
              <Tag
                text={weatherModeStatusToText(unitData?.weatherModeStatus)}
                type={
                  weatherModeStatusToTagType(
                    unitData?.weatherModeStatus,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
              />
            </WinterModeStatus>
            <Button
              disabled
              type={ButtonType.secondary}
              size={ButtonSize.xs}
              block={false}
              onClick={() => {}}
              testId={`${testId}-winter-mode-button`}
            >
              {
                isXs
                  ? t('view.crusher.vsi.drawers.lubrication.accessWinterModeMobile')
                  : t('view.crusher.vsi.drawers.lubrication.accessWinterMode')
              }
            </Button>
          </WinterModeContainer>
        </Col>
      </Row>
    </LubricationContainer>
  );
}

export { LubricationUnit };
